import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { icons, heroVideoSrc, heroPoster } from "./assets";
import { translations } from "./translations";
import { applySeo } from "./seo";
import { IconPin, IconClock, heroFeatureIcons } from "./HeroIcons";
import GallerySection from "./components/sections/GallerySection";
import TestimonialsSection from "./components/sections/TestimonialsSection";
import VisitUsSection from "./components/sections/VisitUsSection";
import NewsletterSection from "./components/sections/NewsletterSection";
import MenuSection from "./components/sections/MenuSection";
import CoffeeSection from "./components/sections/CoffeeSection";
import SiteFooter from "./components/ui/SiteFooter";
import PrimaryButton from "./components/ui/PrimaryButton";
import BookingModal from "./components/booking/BookingModal";
import LandingNav from "./components/layout/LandingNav";
import { galleryTiles } from "./data/galleryTiles";
import { reviews } from "./data/reviews";

function FullSite() {
  const [lang, setLang] = useState("hu");
  const [reduceMotion, setReduceMotion] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = useCallback(() => setBookingOpen(true), []);
  const closeBooking = useCallback(() => setBookingOpen(false), []);
  const heroVideoRef = useRef(null);
  const t = translations[lang];

  useEffect(() => {
    applySeo(lang);
  }, [lang]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduceMotion(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  // useLayoutEffect: mute before paint. Never call video.load() — it cancels
  // iOS Safari’s autoplay privilege and then play() only works after a tap.
  useLayoutEffect(() => {
    if (reduceMotion) return undefined;
    const video = heroVideoRef.current;
    if (!video) return undefined;

    let cancelled = false;
    let retryTimer = 0;

    const armMutedInline = () => {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute("muted", "");
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      video.removeAttribute("controls");
    };

    const tryPlay = () => {
      if (cancelled) return;
      armMutedInline();
      if (!video.paused && !video.ended) return;
      const playPromise = video.play();
      if (playPromise?.catch) {
        playPromise.catch(() => {
          /* Buffering / Low Power Mode — keep retrying from media events. */
        });
      }
    };

    armMutedInline();
    tryPlay();

    const onReady = () => tryPlay();
    video.addEventListener("loadeddata", onReady);
    video.addEventListener("canplay", onReady);
    video.addEventListener("canplaythrough", onReady);
    video.addEventListener("loadedmetadata", onReady);

    // Fallback if autoplay is still gated (e.g. Low Power Mode).
    const onUserGesture = () => tryPlay();
    document.addEventListener("touchstart", onUserGesture, {
      capture: true,
      passive: true,
    });
    document.addEventListener("pointerdown", onUserGesture, {
      capture: true,
    });

    const onVisibility = () => {
      if (document.visibilityState === "visible") tryPlay();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", tryPlay);

    let attempts = 0;
    const tick = () => {
      if (cancelled) return;
      attempts += 1;
      tryPlay();
      if (attempts < 48 && video.paused) {
        retryTimer = window.setTimeout(tick, 200);
      }
    };
    retryTimer = window.setTimeout(tick, 50);

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
      video.removeEventListener("loadeddata", onReady);
      video.removeEventListener("canplay", onReady);
      video.removeEventListener("canplaythrough", onReady);
      video.removeEventListener("loadedmetadata", onReady);
      document.removeEventListener("touchstart", onUserGesture, true);
      document.removeEventListener("pointerdown", onUserGesture, true);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", tryPlay);
    };
  }, [reduceMotion, heroVideoSrc]);

  return (
    <div className="landing">
      <LandingNav lang={lang} onLangChange={setLang} t={t} />

      <main className="hero" id="home" data-nav-section="home">
        {reduceMotion ? (
          <img
            src={heroPoster}
            alt=""
            className="hero-bg-image"
            aria-hidden="true"
          />
        ) : (
          <video
            ref={(el) => {
              heroVideoRef.current = el;
              if (!el) return;
              // React often omits the muted DOM attribute; Safari needs it
              // present before it will honor autoplay without a gesture.
              el.muted = true;
              el.defaultMuted = true;
              el.playsInline = true;
              el.setAttribute("muted", "");
              el.setAttribute("playsinline", "");
              el.setAttribute("webkit-playsinline", "");
            }}
            className="hero-bg-video"
            src={heroVideoSrc}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={heroPoster}
            controls={false}
            disablePictureInPicture
            disableRemotePlayback
            aria-hidden="true"
          />
        )}
        <div className="hero-bg-overlay" aria-hidden="true" />

        <div className="hero-inner container">
          <section className="hero-copy">
            <div className="hero-brand-lockup">
              <img
                src={icons.logoWhite}
                alt={t.logoAlt}
                className="hero-logo-image"
              />
              <h1 className="hero-brand-title">
                <span className="hero-brand-title-rule" aria-hidden="true" />
                <span className="hero-brand-title-text">{t.hero.brandSub}</span>
                <span className="hero-brand-title-rule" aria-hidden="true" />
              </h1>
            </div>
            <p className="tagline">{t.hero.tagline}</p>
            <p className="location">
              <IconPin className="hero-icon hero-icon-pin" />
              <span className="hero-meta-text">{t.visitUs.address}</span>
            </p>
            <p className="hero-hours">
              <IconClock className="hero-icon hero-icon-clock" />
              <span className="hero-meta-text">{t.visitUs.hours}</span>
            </p>

            <div className="hero-features">
              {t.hero.features.map((label, index) => {
                const Icon = heroFeatureIcons[index];
                return (
                  <span key={label} className="hero-feature-pill">
                    <Icon className="hero-icon" />
                    <span>{label}</span>
                  </span>
                );
              })}
            </div>

            <div className="hero-actions">
              <PrimaryButton className="hero-cta" onClick={openBooking}>
                {t.visitUs.bookTable}
              </PrimaryButton>
            </div>
          </section>
        </div>
      </main>

      <MenuSection
        t={t.menu}
        lang={lang}
        instagramUrl={t.visitUs.instagramUrl}
        facebookUrl={t.visitUs.facebookUrl}
      />
      <CoffeeSection t={t.coffee} checkoutT={t.checkout} lang={lang} />
      <GallerySection t={t.gallery} tiles={galleryTiles} />
      <TestimonialsSection t={t.testimonials} reviews={reviews} />
      <VisitUsSection t={t.visitUs} onBookTable={openBooking} />
      <NewsletterSection t={t.newsletter} lang={lang} />
      <SiteFooter t={t.visitUs} />

      <BookingModal open={bookingOpen} onClose={closeBooking} t={t.booking} lang={lang} />
    </div>
  );
}

export default FullSite;
